"use client";

import ImageBox from "@/app/components/ImageBox";
import { Box, Container, Typography, useMediaQuery, useTheme } from "@mui/material";

type LogoConfig = {
    src: string;
    alt: string;
    size?: "small" | "medium" | "large";
};

const logos: LogoConfig[] = [
    { src: "/images/brand-The-Nudge.png", alt: "The Nudge" },
    { src: "/images/brand-skrewball.png", alt: "Skrewball", size: "large" },
    { src: "/images/brand-long_drink.jpg", alt: "Long Drink" },
    { src: "/images/brand-EBBS.png", alt: "EBBS" },
    { src: "/images/brand-wilson.png", alt: "Wilson" },
    { src: "/images/brand-Malibu-logo.png", alt: "Malibu" },
    { src: "/images/brand-shake-shack.png", alt: "Shake Shack" },
    { src: "/images/brand-RedBull.png", alt: "Red Bull" },
    { src: "/images/brand-montauk_2.jpg", alt: "Montauk Brewing" },
    { src: "/images/brand-topo_chico.png", alt: "Topo Chico" },
    { src: "/images/brand-dr-squatch.png", alt: "Dr. Squatch" },
    { src: "/images/brand-denada_tequila.png", alt: "De Nada" },
    { src: "/images/brand-Jameson-Logo.png", alt: "Jameson" },
    { src: "/images/brand-guayaki.png", alt: "Guayakí Yerba Mate", size: "large" },
    { src: "/images/brand-glenlivet.png", alt: "The Glenlivet" },
    { src: "/images/brand-Montenegro.jpg", alt: "Montenegro" },
    // New logos served from AWS S3 object URLs
    {
        src: "https://storage.googleapis.com/cuo-website/Kiehls+Image.png",
        alt: "Kiehl's",
    },
    {
        src: "https://storage.googleapis.com/cuo-website/AriZona+Image.png",
        alt: "AriZona",
        size: "large",
    },
    {
        src: "https://storage.googleapis.com/cuo-website/Nuuly+Image.png",
        alt: "Nuuly",
    },
];

export default function BrandStrip() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"), { noSsr: true });
    const logoSize = isMobile ? 50 : isTablet ? 64 : 77;
    const gap = isMobile ? 20 : isTablet ? 32 : 40;
    const totalWidth = logos.length * (logoSize + gap);

    return (
        <Box
            sx={{
                borderTop: "1px solid rgba(0,0,0,0.08)",
                borderBottom: "1px solid rgba(0,0,0,0.08)",
                py: { xs: 1.5, sm: 2 },
                backgroundColor: "#fff",
                overflow: "hidden",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
            }}
        >
            <Container maxWidth="xl" sx={{ px: 0 }}>
                <Box sx={{ px: { xs: 2, md: 6 } }}>
                    <Typography
                        variant="body2"
                        sx={{
                            color: "#6b7280",
                            textAlign: "center",
                            mb: 1.5,
                            fontWeight: 500,
                            letterSpacing: "0.02em",
                        }}
                    >
                        Trusted By The Best
                    </Typography>
                </Box>
                <Box sx={{ width: "100%", overflow: "hidden" }}>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            alignItems: "center",
                            width: "max-content",
                            gap: `${gap}px`,
                            animation: "brandStripScroll 25s linear infinite",
                            "@keyframes brandStripScroll": {
                                "0%": { transform: "translateX(0)" },
                                "100%": { transform: `translateX(-${totalWidth + gap}px)` },
                            },
                        }}
                    >
                        {[...logos, ...logos, ...logos].map((logo, index) => (
                            <Box
                                key={`${logo.alt}-${index}`}
                                sx={{
                                    flexShrink: 0,
                                    width: logoSize,
                                    height: logoSize,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                {/*
                                  Allow certain logos (e.g., Skrewball, Guayakí) to render larger
                                  while keeping most at the default size.
                                */}
                                {(() => {
                                    const scale =
                                        logo.size === "large" ? 1.25 : logo.size === "small" ? 0.85 : 1;
                                    const sizePx = logoSize * scale;
                                    return (
                                        <ImageBox
                                            src={logo.src}
                                            alt={logo.alt}
                                            sx={{
                                                width: sizePx,
                                                height: sizePx,
                                                maxWidth: sizePx,
                                                maxHeight: sizePx,
                                                objectFit: "contain",
                                                opacity: 0.9,
                                                transition: "opacity 0.3s ease",
                                                "&:hover": { opacity: 1 },
                                            }}
                                        />
                                    );
                                })()}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
